import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";
import { format4, numberWithCommas, separateDecimal, separateNumber, separateNumberParts } from "../utils/numbers";
import useWindowSize from "../hooks/useWindowSize";
import retryApi from "../utils/api";
import { getNavHistory } from "../services/funds.service";
import { useTheme } from "next-themes";
import { useTranslation } from "react-i18next";

export default function DashboardFundItem({ data, onSelect }) {
  const [navMonthlyHistory, setNavMonthlyHistory] = useState(null);
  const [navYearlyHistory, setNavYearlyHistory] = useState(null);

  const [loading, setLoading] = useState(false);

  const { theme } = useTheme();
  const screenSize = useWindowSize();
  const abortRef = useRef(null);

  const { t } = useTranslation();

  const computeMonthlyReturn = useCallback(() => {
    if (!navMonthlyHistory || navMonthlyHistory?.data?.length < 2) return "0";

    let oldest = navMonthlyHistory.data[0];
    let newest = navMonthlyHistory.data[0];

    navMonthlyHistory.data.forEach((item) => {
      if (dayjs(item.nav_time).isBefore(oldest.nav_time)) {
        oldest = item;
      }
      if (dayjs(item.nav_time).isAfter(newest.nav_time)) {
        newest = item;
      }
    });

    if (oldest.nav !== 0) {
      return String(((newest.nav - oldest.nav) / oldest.nav) * 100);
    } else {
      return "0";
    }
  }, [navMonthlyHistory]);

  const computeYearlyReturn = useCallback(() => {
    if (!navYearlyHistory || navYearlyHistory?.data?.length < 2) return "0";

    let oldest = navYearlyHistory.data[0];
    let newest = navYearlyHistory.data[0];

    navYearlyHistory.data.forEach((item) => {
      if (dayjs(item.nav_time).isBefore(oldest.nav_time)) {
        oldest = item;
      }
      if (dayjs(item.nav_time).isAfter(newest.nav_time)) {
        newest = item;
      }
    });

    if (oldest.nav !== 0) {
      return String(((newest.nav - oldest.nav) / oldest.nav) * 100);
    } else {
      return "0";
    }
  }, [navYearlyHistory]);

  const monthlyReturn = separateNumber(computeMonthlyReturn(), { format: true, digits: 2 });
  const yearlyReturn = separateNumber(computeYearlyReturn(), { format: true, digits: 2 });

  const hasNavData = (data.navData && Object.keys(data.navData).length !== 0) ? true : false;

  // console.log(data.navData.offer_price);
  const navRounded = format4(hasNavData ? data.navData?.offer_price || 0 : data.nav);

  const nav = separateDecimal(navRounded);

  // const nav = separateNumberParts(format4(parseFloat(data.navData?.offer_price || "0")));

  useEffect(() => {
    const getFundsNavHistory = async (retries = 3) => {
      if (abortRef.current) {
        abortRef.current.abort();
      }

      const ac = new AbortController();
      abortRef.current = ac;

      setLoading(true);

      const navDate = data?.navData?.nav_date; // e.g. "2025-08-10T00:00:00.000Z"

      // choose navDate if present and valid, otherwise use now
      let today = navDate ? dayjs(navDate) : dayjs();
      if (!today.isValid()) today = dayjs(); // fallback if input was invalid

      const currentDate = today.format("YYYY-MM-DD");
      const prev30Date = today.subtract(30, "day").format("YYYY-MM-DD");
      const prevYearDate = today.subtract(1, "year").format("YYYY-MM-DD");

      try {
        const [monthlyResponse, quarterlyResponse] = await Promise.all([
          retryApi((signal) => getNavHistory(data.symbol_code, prev30Date, currentDate, signal), retries, ac.signal),
          retryApi((signal) => getNavHistory(data.symbol_code, prevYearDate, currentDate, signal), retries, ac.signal),
        ]);

        setNavMonthlyHistory(monthlyResponse?.data);
        setNavYearlyHistory(quarterlyResponse?.data);

        if (monthlyResponse.error && quarterlyResponse.error) {
          // console.log('err after retries', monthlyResponse, quarterlyResponse)
        } else {
          // console.log('no errors', monthlyResponse, quarterlyResponse)
        }
      } catch (err) {
        // console.log(err)
      } finally {
        setLoading(false);
      }
    };

    getFundsNavHistory();
  }, [data]);

  return (
    <div
      className='row w-100 mx-auto align-items-center'
      style={{
        lineHeight: "1.1",
        // backgroundColor: theme === "light" ? "rgba(255, 255, 255, .8)" : "rgb(58, 58, 58)",
        backgroundColor: "var(--theme-card-item-bg)",
        borderRadius: "10px",
        boxShadow: "rgba(0, 0, 0, 0.16) 0px 1px 4px",
        color: theme === "light" ? "rgb(0,0,0)" : "rgb(245,245,245)",
        rowGap: ".8rem",
        padding: screenSize >= 576 ? "1rem" : ".75rem .75rem",
      }}
    >
      <div className='col col-sm p-0' onClick={() => onSelect(() => ({ show: true, symbol: data?.symbol_code }))} style={{ cursor: 'pointer'}}>
        <div
          className='text-truncate'
          style={{
            fontSize: screenSize >= 576 ? "1.35rem" : "1.05rem",
            lineHeight: "1.1",
            fontWeight: "500",
            overflow: "hidden",
            color: theme === "dark" ? "var(--theme-light-secondary)" : "var(--theme-light-primary)",
          }}
        >
          {data.symbol_code}
        </div>
        <div
          className=''
          style={{
            fontSize: screenSize >= 576 ? ".82rem" : ".78rem",
            fontWeight: "400",
            lineHeight: "1.1",
            color: theme === "dark" ? "rgba(255,255,255,.53)" : "var(--theme-light-secondary)",
            marginTop: screenSize >= 576 ? "4px" : "3px",
            display: "-webkit-box", // establish a WebKit flexbox container
            WebkitBoxOrient: "vertical", // stack children vertically
            WebkitLineClamp: 2, // limit to 2 lines
            overflow: "hidden", // hide overflowing text
            // width: `${width}px`,                // optional width control
          }}
        >
          {data.fund_name}
        </div>
      </div>
      <div className='col col-sm p-0 text-right-fixed'>
        <div
          className=''
          style={{
            fontSize: screenSize >= 576 ? "0.8rem" : ".7rem",
            color: theme === "light" ? "rgba(0,0,0,0.45)" : "rgba(255,255,255,0.6)",
            minHeight: screenSize >= 576 ? '' : '25px'
          }}
        >
          {t("app.fund.price")}
        </div>
        <div
          dir="ltr"
          className='mt-1'
          style={{
            fontSize: screenSize >= 576 ? "1.2rem" : "1.05rem",
          }}
        >
          <span
            dir="ltr"
            className=''
            style={{ fontSize: screenSize >= 576 ? ".8rem" : ".7rem", marginRight: ".25rem", color: theme === "light" ? "var(--theme-light-primary)" : "rgb(229, 124, 236)" }}
          >
            {t("app.fund.rupee")}
          </span>
          {nav.whole == "0" ? "0" : numberWithCommas(nav.whole) + "."}
          <span style={{ fontSize: screenSize >= 576 ? "0.9rem" : ".72rem" }}>{nav.whole == "0" ? "" : nav.decimal}</span>
        </div>
      </div>
      <div className='col col-sm p-0 text-right-fixed'>
        <div
          className=''
          style={{
            fontSize: screenSize >= 576 ? "0.8rem" : ".7rem",
            color: theme === "light" ? "rgba(0,0,0,0.45)" : "rgba(255,255,255,0.6)",
            minHeight: screenSize >= 576 ? '' : '25px'
          }}
        >
          {t("app.fund.monthly")}
        </div>
        <div
          className='mt-1'
          style={{
            fontSize: screenSize >= 576 ? "1.2rem" : "1.05rem",
          }}
        >
          {numberWithCommas(monthlyReturn.whole) + "."}
          <span style={{ fontSize: screenSize >= 576 ? "0.9rem" : ".72rem" }}>{monthlyReturn.decimal}</span>
          <span
            className=''
            style={{ fontSize: screenSize >= 576 ? ".8rem" : ".7rem", marginLeft: ".25rem", color: theme === "light" ? "var(--theme-light-primary)" : "rgb(229, 124, 236)" }}
          >
            %
          </span>
        </div>
      </div>
	  <div className='col col-sm p-0 text-right-fixed'>
        <div
          className=''
          style={{
            fontSize: screenSize >= 576 ? "0.8rem" : ".7rem",
            color: theme === "light" ? "rgba(0,0,0,0.45)" : "rgba(255,255,255,0.6)",
            minHeight: screenSize >= 576 ? '' : '25px'
          }}
        >
          {t("app.fund.yearly")}
        </div>
        <div
          className='mt-1'
          style={{
            fontSize: screenSize >= 576 ? "1.2rem" : "1.05rem",
          }}
        >
          {numberWithCommas(yearlyReturn.whole) + '.'}
          <span style={{ fontSize: screenSize >= 576 ? "0.9rem" : ".72rem" }}>{yearlyReturn.decimal}</span>
          <span
            className=''
            style={{ fontSize: screenSize >= 576 ? ".8rem" : ".7rem", marginLeft: ".25rem", color: theme === "light" ? "var(--theme-light-primary)" : "rgb(229, 124, 236)" }}
          >
            %
          </span>
        </div>
      </div>
    </div>
  );
}
